import { useState, useEffect } from 'react';
import {
  adminFinanceService,
  SettlementQuery,
  SettlementListResponse,
  FinancialDashboard,
  Commission,
} from '@services/adminFinanceService';

export function useFinancialDashboard(period: 'today' | 'week' | 'month' = 'month') {
  const [data, setData] = useState<FinancialDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const dashboard = await adminFinanceService.getFinancialDashboard(period);
        if (isMounted) {
          setData(dashboard);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Dashboard yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    const interval = setInterval(loadDashboard, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [period]);

  return { data, isLoading, error };
}

export function useSettlements(query: SettlementQuery) {
  const [data, setData] = useState<SettlementListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadSettlements = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await adminFinanceService.listSettlements(query);
        if (isMounted) {
          setData(result);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Ödemeler yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSettlements();

    return () => {
      isMounted = false;
    };
  }, [query.status, query.restaurantId, query.startDate, query.endDate, query.page, query.limit]);

  return { data, isLoading, error };
}

export function useCommissions(restaurantId?: string, startDate?: string, endDate?: string) {
  const [data, setData] = useState<Commission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCommissions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const commissions = await adminFinanceService.listCommissions(restaurantId, startDate, endDate);
        if (isMounted) {
          setData(commissions);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Komisyonlar yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCommissions();

    return () => {
      isMounted = false;
    };
  }, [restaurantId, startDate, endDate]);

  return { data, isLoading, error };
}
